import { useEffect } from 'react';
import { create } from 'zustand';
import Logger from 'utils/logger';
import { isAbortError } from '../utils/isAbortError';

type AudioPreviewState = {
  playingAudioId: string | null;
  isBuffering: boolean;
  errorAudioId: string | null;
  setPlaying: (audioId: string | null) => void;
  setBuffering: (isBuffering: boolean) => void;
  setError: (audioId: string | null) => void;
};

const useAudioPreviewStore = create<AudioPreviewState>((set) => ({
  playingAudioId: null,
  isBuffering: false,
  errorAudioId: null,
  setPlaying: (playingAudioId) => set({ playingAudioId, isBuffering: false }),
  setBuffering: (isBuffering) => set({ isBuffering }),
  setError: (errorAudioId) =>
    set({ errorAudioId, playingAudioId: null, isBuffering: false }),
}));

let sharedAudio: HTMLAudioElement | null = null;

function getSharedAudio(): HTMLAudioElement {
  if (sharedAudio) return sharedAudio;

  const audio = new Audio();
  audio.preload = 'none';

  const { setPlaying, setBuffering, setError } =
    useAudioPreviewStore.getState();

  audio.addEventListener('waiting', () => setBuffering(true));
  audio.addEventListener('playing', () => setBuffering(false));
  audio.addEventListener('ended', () => setPlaying(null));
  /**
   * Switching tracks pauses the previous one, and that `pause` event lands
   * after the next track has already started. It is stale once `paused` is
   * false again.
   */
  audio.addEventListener('pause', () => {
    if (audio.paused && !audio.ended) setPlaying(null);
  });
  audio.addEventListener('error', () => {
    setError(audio.dataset.audioId ?? null);
  });

  sharedAudio = audio;

  return audio;
}

export type AudioPreviewTrackState = {
  isPlaying: boolean;
  isBuffering: boolean;
  hasFailed: boolean;
};

/**
 * Selected per track so that starting or stopping playback only re-renders the
 * two rows whose state actually changed.
 */
export function useAudioPreviewTrack(audioId: string): AudioPreviewTrackState {
  const isPlaying = useAudioPreviewStore(
    (state) => state.playingAudioId === audioId,
  );
  const isBuffering = useAudioPreviewStore(
    (state) => state.playingAudioId === audioId && state.isBuffering,
  );
  const hasFailed = useAudioPreviewStore(
    (state) => state.errorAudioId === audioId,
  );

  return { isPlaying, isBuffering, hasFailed };
}

export function useStopAudioPreviewOnUnmount(): void {
  useEffect(() => stopAudioPreview, []);
}

export function toggleAudioPreview(audioId: string, src: string | null): void {
  if (!src) return;

  const audio = getSharedAudio();
  const { playingAudioId, setPlaying, setBuffering, setError } =
    useAudioPreviewStore.getState();

  if (playingAudioId === audioId) {
    audio.pause();
    setPlaying(null);

    return;
  }

  audio.pause();
  audio.dataset.audioId = audioId;
  /** Assigning src aborts any in-flight request for the previous track. */
  audio.src = src;
  audio.currentTime = 0;

  setError(null);
  setPlaying(audioId);
  setBuffering(true);

  audio.play().catch((error) => {
    if (isAbortError(error) || audio.dataset.audioId !== audioId) return;

    Logger.error(error);
    setError(audioId);
  });
}

export function stopAudioPreview(): void {
  const { setPlaying } = useAudioPreviewStore.getState();
  setPlaying(null);

  if (!sharedAudio) return;

  sharedAudio.pause();
  /**
   * pause() alone leaves the download running; clearing src and reloading is
   * what actually cancels it.
   */
  sharedAudio.removeAttribute('src');
  sharedAudio.load();
}
